// Find in Project (⇧⌘F; the Mac's FindInProjectView and LibraryModel+Find): literal or regular expression, match case,
// whole words, over the manuscript, all text or the open text — the stored Markdown, as the editor shows it. Matches are
// listed by scene with a little text either side (1,000 at most); choosing one selects it in Write. Replace All records
// a "Before replacing “…”" checkpoint, then splices only the matched characters (anchors elsewhere hold), all in one
// edit; the open editor follows at once.
import { useEffect, useMemo, useRef, useState } from 'react';
import { findMatches, InvalidPattern, replaceLabel, sliceScalars, scalarLength, type DocumentEditor, type FindMatch, type FindQuery } from '@writing-suite/core';
import { announce } from '../shell/context.ts';
import type { Workspace } from '../shell/workspace.ts';
import { ConfirmDialog } from './Menu.tsx';
import { requestCollection } from '../store/plan.ts';
import { documentNoun, nounCount } from '../store/binderFilter.ts';

type Scope = 'manuscript' | 'all' | 'open';
const LIMIT = 1000;

interface Passage {
  fragmentId: string;
  title: string;
  match: FindMatch;
  before: string;
  after: string;
}

/** The fragments a scope covers, in reading order (`fragmentIDs(for:)`). */
export function scopeFragments(ws: Pick<Workspace, 'project' | 'shownFragments'>, scope: Scope): string[] {
  if (scope === 'open') return ws.shownFragments;
  return ws.project.counted(scope === 'all');
}

/** Every match in the scope, with context (`LibraryModel.find`). */
export function findPassages(ws: Pick<Workspace, 'project' | 'shownFragments' | 'live'>, query: FindQuery, template: string, scope: Scope): { passages: Passage[]; truncated: boolean } {
  const passages: Passage[] = [];
  for (const id of scopeFragments(ws, scope)) {
    const f = ws.project.fragment(id);
    if (!f) continue;
    const text = ws.live ? ws.live.text(id) : f.text;
    const matches = findMatches(query, text, template);
    if (!matches.length) continue;
    const length = scalarLength(text);
    for (const m of matches) {
      if (passages.length >= LIMIT) return { passages, truncated: true };
      const from = Math.max(0, m.start - 40), to = Math.min(length, m.end + 40);
      const flat = (s: string) => s.replace(/\n/g, ' ');
      passages.push({
        fragmentId: id,
        title: f.title || 'Untitled',
        match: m,
        before: (from > 0 ? '…' : '') + flat(sliceScalars(text, from, m.start)),
        after: flat(sliceScalars(text, m.end, to)) + (to < length ? '…' : ''),
      });
    }
  }
  return { passages, truncated: false };
}

export function FindPanel({ workspace: ws, narrow, onClose }: { workspace: Workspace; narrow: boolean; onClose: () => void }) {
  const [text, setText] = useState('');
  const [replacement, setReplacement] = useState('');
  const [matchCase, setMatchCase] = useState(false);
  const [wholeWords, setWholeWords] = useState(false);
  const [regex, setRegex] = useState(false);
  const [scope, setScope] = useState<Scope>('manuscript');
  const [confirming, setConfirming] = useState(false);
  const [query, setQuery] = useState<FindQuery>({ text: '' });
  const field = useRef<HTMLInputElement>(null);
  const panel = useRef<HTMLElement>(null);

  // Searching waits for a pause in typing the query.
  useEffect(() => {
    const t = setTimeout(() => setQuery({ text, matchCase, wholeWords, regularExpression: regex }), 200);
    return () => clearTimeout(t);
  }, [text, matchCase, wholeWords, regex]);
  useEffect(() => field.current?.focus(), []);

  const result = useMemo((): { passages: Passage[]; truncated: boolean } | { error: string } => {
    if (!query.text) return { passages: [], truncated: false };
    try {
      return findPassages(ws, query, replacement, scope);
    } catch (e) {
      return { error: e instanceof InvalidPattern ? e.message : String(e) };
    }
    // Found again when the project changes (another device, a replace) or the scope does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, replacement, scope, ws.project, scope === 'open' ? ws.shownFragments : null]);

  const passages = 'passages' in result ? result.passages : [];
  const scenes = new Set(passages.map((p) => p.fragmentId)).size;
  const groups: { fragmentId: string; title: string; passages: Passage[] }[] = [];
  for (const p of passages) {
    const last = groups[groups.length - 1];
    if (last && last.fragmentId === p.fragmentId) last.passages.push(p);
    else groups.push({ fragmentId: p.fragmentId, title: p.title, passages: [p] });
  }
  const changing = passages.filter((p) => p.match.text !== p.match.replacement);

  const replaceAll = () => {
    const live = ws.live;
    if (!live) return;
    // Matched afresh against the text as it is now, in the same turn as the edit.
    const q = { text, matchCase, wholeWords, regularExpression: regex };
    const pending = scopeFragments(ws, scope).flatMap((id) => {
      const ms = findMatches(q, live.text(id), replacement).filter((m) => m.text !== m.replacement);
      return ms.length ? [{ id, ms }] : [];
    });
    if (!pending.length) return;
    const label = replaceLabel(text);
    live.edit((e) => {
      e.createCheckpoint(`Before replacing “${label}”`);
      for (const { id, ms } of pending) {
        for (const m of [...ms].sort((a, b) => b.from - a.from)) e.spliceText(id, m.from, m.to - m.from, m.replacement);
        (e as unknown as DocumentEditor).touch(id, `Replace “${label}”`);
      }
    });
    const n = pending.reduce((k, p) => k + p.ms.length, 0);
    announce(`Replaced ${n} ${n === 1 ? 'match' : 'matches'}`);
    setQuery({ ...q });
  };

  return (
    <aside
      ref={panel}
      className={`find-panel${narrow ? ' sheet' : ''}`}
      role="dialog"
      aria-modal="false"
      aria-labelledby="find-title"
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !confirming) {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <header className="inspector-head">
        <h2 id="find-title" className="eyebrow">
          Find in Project
        </h2>
        <button type="button" className="quiet-button" onClick={onClose}>
          Done
        </button>
      </header>
      <form
        className="find-form"
        onSubmit={(e) => {
          e.preventDefault();
          setQuery({ text, matchCase, wholeWords, regularExpression: regex });
        }}
      >
        <label htmlFor="find-text">Find</label>
        <input id="find-text" ref={field} type="search" value={text} onChange={(e) => setText(e.target.value)} autoComplete="off" spellCheck={false} />
        <label htmlFor="find-replace">Replace with</label>
        <input id="find-replace" type="text" value={replacement} onChange={(e) => setReplacement(e.target.value)} autoComplete="off" spellCheck={false} />
        <fieldset className="find-options">
          <legend className="visually-hidden">Options</legend>
          <label>
            <input type="checkbox" checked={matchCase} onChange={(e) => setMatchCase(e.target.checked)} /> Match case
          </label>
          <label>
            <input type="checkbox" checked={wholeWords} onChange={(e) => setWholeWords(e.target.checked)} /> Whole words
          </label>
          <label>
            <input type="checkbox" checked={regex} onChange={(e) => setRegex(e.target.checked)} /> Regular expression
          </label>
        </fieldset>
        <label htmlFor="find-scope">In</label>
        <select id="find-scope" value={scope} onChange={(e) => setScope(e.target.value as Scope)}>
          <option value="manuscript">Manuscript</option>
          <option value="all">All text</option>
          <option value="open">Open text</option>
        </select>
      </form>
      <div className="find-status" role="status" data-matches={passages.length} data-scenes={scenes}>
        {'error' in result ? (
          <span className="error">{result.error}</span>
        ) : query.text ? (
          `${passages.length === 0 ? 'No matches' : `${passages.length}${result.truncated ? '+' : ''} ${passages.length === 1 ? 'match' : 'matches'} in ${nounCount(documentNoun(ws.project.type, scope !== 'all'), scenes)}`}`
        ) : (
          ''
        )}
      </div>
      {ws.canWrite && changing.length ? (
        <button type="button" className="button primary small replace-all" onClick={() => setConfirming(true)}>
          Replace All
        </button>
      ) : null}
      {ws.canWrite && query.text && !query.regularExpression && scope !== 'open' ? (
        <button
          type="button"
          className="quiet-button save-collection"
          onClick={() => {
            requestCollection({ text: query.text, scope: scope === 'all' ? 'all-text' : 'manuscript' });
            onClose();
          }}
        >
          Save as Collection
        </button>
      ) : null}
      <div className="find-results">
        {groups.map((g) => (
          <section key={g.fragmentId} className="find-scene" aria-label={g.title}>
            <h3 className="find-scene-title">{g.title}</h3>
            <ul>
              {g.passages.map((p) => (
                <li key={`${p.match.start}`}>
                  <button
                    type="button"
                    className="find-passage"
                    onClick={() => {
                      ws.reveal({ fragmentId: p.fragmentId, from: p.match.from, to: p.match.to });
                      if (narrow) onClose();
                    }}
                  >
                    <span className="find-context">{p.before}</span>
                    <mark>{p.match.text}</mark>
                    {replacement && p.match.replacement !== p.match.text ? <ins>{p.match.replacement}</ins> : null}
                    <span className="find-context">{p.after}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
      {confirming ? (
        <ConfirmDialog
          title={`Replace ${changing.length} ${changing.length === 1 ? 'match' : 'matches'}?`}
          message={
            <p>
              Every match of “{text}” {scope === 'manuscript' ? 'in the manuscript' : scope === 'all' ? 'in all text' : 'in the open text'} becomes “{replacement}”. A checkpoint “Before
              replacing “{replaceLabel(text)}”” is made first, so History shows what changed.
            </p>
          }
          confirm="Replace All"
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false);
            replaceAll();
          }}
        />
      ) : null}
    </aside>
  );
}
